import { MessagePayload, deleteToken, getToken, onMessage } from 'firebase/messaging';

import { formatDate } from 'src/utils/date-util';
import { getConsoleDevice, setConsoleDevice, updateConsoleDevice } from 'src/utils/firebase/firebase-short-cut';
import { FirebaseManager } from 'src/utils/firebase/firebase-manager';
import { getBrowserId } from 'src/utils/notifications/browserId';

const firebaseManager = FirebaseManager.getInstance();

const VAPID_KEY = process.env.REACT_APP_FCM_VAPID_KEY;
const SW_URL = '/firebase-messaging-sw.js';

const nowKstIso = () => formatDate(new Date(), "yyyy-MM-dd'T'HH:mm:ss+0900");

/**
 * 브라우저 useragent에서 대략적인 브라우저/OS 이름을 뽑아낸다.
 * 디버깅 보조용이므로 정확도보다는 가독성 우선.
 */
const detectBrowserAndOs = (): { browserName?: string; osName?: string } => {
  if (typeof navigator === 'undefined') return {};
  const ua = navigator.userAgent;
  let browserName: string | undefined;
  if (/Edg\//.test(ua)) browserName = 'Edge';
  else if (/Chrome\//.test(ua) && !/Chromium/.test(ua)) browserName = 'Chrome';
  else if (/Safari\//.test(ua) && !/Chrome|Chromium/.test(ua)) browserName = 'Safari';
  else if (/Firefox\//.test(ua)) browserName = 'Firefox';

  let osName: string | undefined;
  if (/iPhone|iPad|iPod/.test(ua)) osName = 'iOS';
  else if (/Android/.test(ua)) osName = 'Android';
  else if (/Mac OS X/.test(ua)) osName = 'macOS';
  else if (/Windows/.test(ua)) osName = 'Windows';
  else if (/Linux/.test(ua)) osName = 'Linux';

  return { browserName, osName };
};

/**
 * Service Worker 등록 보장 + activation 대기.
 * register() Promise는 installing 시점에 resolve되므로 그 상태로 getToken에 넘기면
 * push 라우팅이 active SW와 연결되지 않을 수 있다. `serviceWorker.ready`로 activated 상태를 보장한다.
 */
const ensureSwRegistration = async (): Promise<ServiceWorkerRegistration | null> => {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return null;
  try {
    const existing = await navigator.serviceWorker.getRegistration(SW_URL);
    if (!existing) {
      await navigator.serviceWorker.register(SW_URL);
    }
    return await navigator.serviceWorker.ready;
  } catch (error) {
    console.warn('[ensureSwRegistration] failed', error);
    return null;
  }
};

/**
 * Notification permission 상태 — 미지원 환경 포함.
 */
const getPermissionSnapshot = (): 'default' | 'granted' | 'denied' => {
  if (typeof window === 'undefined' || !('Notification' in window)) return 'denied';
  return Notification.permission;
};

/**
 * admin 로그인 시 호출. 권한 'granted' + 브라우저 지원 시 FCM 토큰을 발급받아
 * `consoleDevice/{browserId}`에 upsert한다. 실패는 silent skip.
 */
export const requestFcmTokenAndRegister = async (uid: string): Promise<void> => {
  if (!VAPID_KEY) {
    console.warn('[requestFcmTokenAndRegister] REACT_APP_FCM_VAPID_KEY missing — skip');
    return;
  }
  if (getPermissionSnapshot() !== 'granted') return;

  const messaging = await firebaseManager.getMessagingSafe();
  if (!messaging) return;

  const swRegistration = await ensureSwRegistration();
  if (!swRegistration) return;

  let token: string;
  try {
    token = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: swRegistration,
    });
  } catch (error) {
    console.warn('[requestFcmTokenAndRegister] getToken failed', error);
    return;
  }

  if (!token) return;

  const browserId = getBrowserId();
  if (!browserId) return;

  const { browserName, osName } = detectBrowserAndOs();
  const now = nowKstIso();

  try {
    await setConsoleDevice(browserId, {
      token,
      userId: uid,
      active: true,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
      browserName,
      osName,
      updatedAt: now,
      latestSignedInAt: now,
      permissions: {
        notification: 'granted',
      },
    });
  } catch (error) {
    console.warn('[requestFcmTokenAndRegister] setConsoleDevice failed', error);
  }
};

/**
 * 로그아웃 직전 호출. consoleDevice 도큐먼트를 비활성화하고 클라이언트 토큰을 삭제한다.
 * fbSignOut() 이후엔 Firestore write 권한이 없으므로 반드시 sign-out 전에 호출.
 */
export const unregisterFcmToken = async (): Promise<void> => {
  const browserId = getBrowserId();
  if (!browserId) return;

  try {
    const existing = await getConsoleDevice(browserId);
    if (existing) {
      await updateConsoleDevice(browserId, {
        active: false,
        token: null,
        updatedAt: nowKstIso(),
      });
    }
  } catch (error) {
    console.warn('[unregisterFcmToken] updateConsoleDevice failed', error);
  }

  try {
    const messaging = await firebaseManager.getMessagingSafe();
    if (messaging) await deleteToken(messaging);
  } catch (error) {
    console.warn('[unregisterFcmToken] deleteToken failed', error);
  }
};

export type ForegroundFcmHandler = (payload: MessagePayload) => void;

/**
 * Foreground onMessage 구독. 반환값 호출로 unsubscribe.
 * messaging 미지원 환경에선 noop unsubscribe.
 */
export const onForegroundFcmMessage = (handler: ForegroundFcmHandler): (() => void) => {
  let unsubscribe: (() => void) | null = null;
  let cancelled = false;

  firebaseManager
    .getMessagingSafe()
    .then((messaging) => {
      if (cancelled || !messaging) return;
      unsubscribe = onMessage(messaging, handler);
    })
    .catch((error) => {
      console.warn('[onForegroundFcmMessage] subscribe failed', error);
    });

  return () => {
    cancelled = true;
    if (unsubscribe) {
      unsubscribe();
      unsubscribe = null;
    }
  };
};
